import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { SectionStyle } from './useSectionStyles';

interface StyleProfile {
  id: string;
  name: string;
  styles: SectionStyle[];
  is_default: boolean;
  created_at: string;
}

export function useStyleProfiles() {
  const [profiles, setProfiles] = useState<StyleProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('style_profiles')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setProfiles(data || []);
    } catch (err) {
      console.error('Error loading style profiles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load style profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const saveProfile = async (name: string, styles: SectionStyle[], makeDefault: boolean = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // If making this profile default, unset any existing default
      if (makeDefault) {
        await supabase
          .from('style_profiles')
          .update({ is_default: false })
          .eq('user_id', user.id)
          .eq('is_default', true);
      }

      const { data, error } = await supabase
        .from('style_profiles')
        .insert([{
          user_id: user.id,
          name,
          styles,
          is_default: makeDefault
        }])
        .select()
        .single();

      if (error) throw error;
      await loadProfiles();
      return data;
    } catch (err) {
      console.error('Error saving style profile:', err);
      throw err;
    }
  };

  const updateProfile = async (id: string, updates: Partial<Omit<StyleProfile, 'id' | 'user_id'>>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // If making this profile default, unset any existing default
      if (updates.is_default) {
        await supabase
          .from('style_profiles')
          .update({ is_default: false })
          .eq('user_id', user.id)
          .eq('is_default', true);
      }

      const { error } = await supabase
        .from('style_profiles')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      await loadProfiles();
    } catch (err) {
      console.error('Error updating style profile:', err);
      throw err;
    }
  };

  const deleteProfile = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('style_profiles')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      await loadProfiles();
    } catch (err) {
      console.error('Error deleting style profile:', err);
      throw err;
    }
  };

  return {
    profiles,
    loading,
    error,
    saveProfile,
    updateProfile,
    deleteProfile,
    refresh: loadProfiles
  };
}